
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LogOut, Sparkles } from 'lucide-react';

interface OnboardingLayoutProps {
  children: React.ReactNode;
}

export const OnboardingLayout = ({ children }: OnboardingLayoutProps) => {
  const { signOut, user } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0 -z-10">
        {/* Main gradient orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-emerald-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-32 w-64 h-64 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 left-40 w-80 h-80 bg-gradient-to-br from-amber-500/30 to-orange-500/30 rounded-full blur-3xl animate-pulse delay-2000"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Additional smaller orbs for more dynamic effect */}
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-gradient-to-br from-yellow-500/20 to-red-500/20 rounded-full blur-xl animate-pulse delay-3000"></div>
        <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-2xl animate-pulse delay-4000"></div>
      </div>

      {/* Enhanced Grid Pattern */}
      <div className="absolute inset-0 -z-10 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0),
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px, 25px 25px, 25px 25px'
        }}></div>
      </div>

      {/* Enhanced Header */}
      <header className="absolute top-0 left-0 right-0 p-6 z-30 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          {/* Enhanced Logo/Brand */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-xl blur-lg opacity-75"></div>
              <div className="relative w-14 h-14 bg-gradient-to-br from-emerald-500 via-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-2xl border border-white/20">
                <Sparkles className="w-7 h-7 text-white animate-pulse" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-emerald-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                Trade Bot 365
              </h1>
              <p className="text-sm font-bold text-emerald-400 tracking-wider">PIONEER REVOLUTION</p>
            </div>
          </div>

          {/* Enhanced User Info & Logout */}
          <div className="flex items-center space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-lg font-bold text-white">{user?.email}</p>
              <p className="text-sm text-emerald-400 font-bold tracking-wider">SIÊU ĐỘCQUYỀN</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg border border-white/20 hover:bg-white/20 transition-all duration-300 px-6 py-3 rounded-xl"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline text-sm font-bold">Đăng xuất</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-start justify-center min-h-screen p-6 pt-28 overflow-auto custom-scrollbar scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
        <div className="w-full max-w-7xl">
          {children}
        </div>
      </main>

      {/* Enhanced Footer */}
      <footer className="absolute bottom-0 left-0 right-0 p-6 text-center">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl px-8 py-4 border border-white/10 inline-block shadow-2xl">
          <p className="text-lg font-bold text-white">
            © 2024 Trade Bot 365. 
            <span className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent ml-2">
              Chương trình Đối tác Tiên phong Siêu Độcquyền.
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
};
